import { ChevronRightIcon } from "@chakra-ui/icons";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Flex,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
	useEarmold,
	useEarmoldVariantsPaginated,
} from "../hooks/earmolds.hooks";
import PageTitle from "../components/PageTitle";
import DataTable, { Cell } from "../components/table/ServerSideTable";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useRef } from "react";
import { Variant } from "../types/earmold.types";
import SimpleModal from "../components/modals/SimpleModal";
import VariantForm, {
	VariantFormHandler,
} from "../components/forms/VariantForm";
import { useMutation, useQueryClient } from "react-query";
import {
	createEarmoldVariants,
	updateEarmold,
} from "../mutations/earmold.mutations";
import EarmoldForm, {
	EarmoldFormHandler,
} from "../components/forms/EarmoldForm";

const ProductVariantsPage = () => {
	const { id } = useParams();
	const { data: earmold } = useEarmold(id);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const earmoldModal = useDisclosure();
	const variantModal = useDisclosure();
	const variantFormRef = useRef<VariantFormHandler>(null);
	const { mutate: mutateCreateVariant } = useMutation(createEarmoldVariants, {
		onSuccess: (data) => {
			variantModal.onClose();
			queryClient.invalidateQueries("earmold-variants");
			if (data && data.id != null) {
				navigate(`/products/earmolds/${id}/variants/${data.id}`);
			}
		},
	});
	const { mutate: mutateUpdateEarmold } = useMutation(updateEarmold, {
		onSuccess: () => {
			earmoldModal.onClose();
			queryClient.invalidateQueries("earmold-variants");
		},
	});
	const earmoldFormRef = useRef<EarmoldFormHandler>(null);
	const columns = useMemo<ColumnDef<Variant>[]>(() => {
		return [
			{
				accessorFn: (row) => (
					<Text textTransform={"capitalize"}>{row.label}</Text>
				),
				id: "name",
				header: "Nom du variant",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: () => <>---</>,
				id: "detail",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		];
	}, []);
	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/products"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Produits
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						{earmold && earmold.label}
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<Flex mt={4} justifyContent={"space-between"}>
				<PageTitle>Listing des variants</PageTitle>
				<Flex gap={4}>
					<Button onClick={earmoldModal.onOpen} w="100%" px={"1rem"}>
						Modifier
					</Button>
					<Button
						onClick={variantModal.onOpen}
						w="100%"
						px="3rem"
						bg="brand.700"
						color="white"
					>
						Ajouter un variant
					</Button>
				</Flex>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={useEarmoldVariantsPaginated(id)}
				w={["20rem", "auto"]}
				flexGrow={[0, 1]}
				cellLink={(cell: Cell) => {
					return `/products/earmolds/${id}/variants/${cell.row.id}`;
				}}
			></DataTable>

			<SimpleModal
				isOpen={earmoldModal.isOpen}
				onClose={earmoldModal.onClose}
				title="Modifier l'embout"
				onClick={async () => {
					if (earmoldFormRef.current) {
						const valid = await earmoldFormRef.current.valid();
						if (valid) {
							mutateUpdateEarmold({
								id,
								earmold: earmoldFormRef.current.values(),
							});
						}
					}
				}}
			>
				<EarmoldForm value={earmold} ref={earmoldFormRef} />
			</SimpleModal>

			<SimpleModal
				isOpen={variantModal.isOpen}
				onClose={variantModal.onClose}
				title={"Ajouter un variant"}
				onClick={async () => {
					if (id && variantFormRef.current) {
						const valid = await variantFormRef.current.valid();
						if (valid) {
							mutateCreateVariant({
								id,
								variant: variantFormRef.current.values(),
							});
						}
					}
				}}
			>
				<VariantForm ref={variantFormRef}></VariantForm>
			</SimpleModal>
		</>
	);
};

export default ProductVariantsPage;
